import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import * as Api from '@Api/index';
import * as ApiCategory from '@Api/Category';
import { filterSearchParams } from '@Queries/helpers/generateQueryKeys';
import { useRequest } from '@Contexts/Request';
import useNavigation from '@Navigation/useNavigation';

import type { InfiniteData, QueryClient, UseInfiniteQueryResult } from '@tanstack/react-query';
import type { CatalogData } from '@Types/Catalog';

export interface Params {
  slug: string;
}

interface PrefetchCategoryParams {
  search: string;
  slug: string;
  pageNumber: number;
}

export type Result = UseInfiniteQueryResult<InfiniteData<CatalogData>>;

export function getKeys(input: {
  slug: string;
  typeOfLoading: string;
  search: string;
  page: number;
  region: string;
}) {
  const { slug, search, page, typeOfLoading, region } = input;
  const filteredSearch = filterSearchParams(search);

  const keys = [
    'infiniteCategory',
    { slug, typeOfLoading, page: typeOfLoading === 'auto' ? undefined : page },
    { region },
    filteredSearch,
  ].filter(Boolean);

  return keys;
}

function getCachedData(input: {
  slug: string;
  queryClient: QueryClient;
  search: string;
  page: number;
  region: string;
}): {
  typeOfLoading: string;
  data: CatalogData | null;
} {
  const { slug, queryClient, page, search, region } = input;
  // AUTO
  const keysAuto = getKeys({ slug, typeOfLoading: 'auto', page, search, region });
  const cacheAuto = queryClient.getQueryData<Record<string, CatalogData[]>>(keysAuto);

  if (cacheAuto) return { typeOfLoading: 'auto', data: cacheAuto.pages[0] };

  // PAGINATOR
  const keysPaginator = getKeys({ slug, typeOfLoading: 'paginator', page, search, region });
  const cachePaginator = queryClient.getQueryData<Record<string, CatalogData[]>>(keysPaginator);

  if (cachePaginator) return { typeOfLoading: 'paginator', data: cachePaginator.pages[0] };

  if (!cacheAuto && !cachePaginator)
    return { typeOfLoading: page > 1 ? 'paginator' : 'auto', data: null };
}

// Информация о категории товаров
export const prefetchInfiniteCategory = (
  params: PrefetchCategoryParams,
  queryClient: QueryClient,
) => {
  const request = Api.getRequest();
  const { search, slug, pageNumber } = params;
  const { region } = request;
  const { data, typeOfLoading } = getCachedData({
    slug,
    queryClient,
    search,
    page: pageNumber,
    region,
  });
  const keys = getKeys({
    slug,
    typeOfLoading,
    search,
    page: pageNumber,
    region,
  });

  if (data) return Promise.resolve(data);

  return queryClient.prefetchInfiniteQuery({
    queryKey: keys,
    queryFn: ({ pageParam }) => {
      return ApiCategory.getProducts({
        slug,
        page: pageNumber || pageParam,
        search,
        isInit: true,
        request,
      });
    },
    initialPageParam: 0,
  });
};

export const useInfiniteCategory = (params: Params): Result => {
  const { slug } = params || {};
  const queryClient = useQueryClient();
  const { pathname, search } = useNavigation();
  const request = useRequest();
  const { region } = request;
  const matches = pathname.match(/.*page-([0-9]{0,})$/);
  const page = Number(matches ? matches[1] : 1);

  const { typeOfLoading, data } = getCachedData({ slug, queryClient, search, page, region });
  const keys = getKeys({ slug, typeOfLoading, search, page, region });

  const category = useInfiniteQuery({
    queryKey: keys,
    queryFn: ({ pageParam }) => {
      if (data && !pageParam) return Promise.resolve(data);

      return ApiCategory.getProducts({
        slug,
        page: pageParam || page,
        search,
        isInit: !pageParam,
        request,
      });
    },
    initialPageParam: 0,
    enabled: true,
    placeholderData: (previousData) => previousData,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return null;

      const nextPageParam = lastPage.page + 1;

      return nextPageParam > lastPage.pageCount ? null : nextPageParam;
    },
  });

  return category;
};
